import { isDateString } from 'class-validator';
import { isDate, isNil, isObject, map, reduce, sortBy } from 'lodash';

import { AppointmentType } from './appointment-type';
import type { Maybe, Nullable } from './globals';
import { objectFilter } from './js-helpers';
import { type DateLike, NovaDate } from './nova-date';
import type { ISettings } from './settings';
import { isFutureDate } from './timezone';

export enum AppointmentStatus {
  NoShow = 'NoShow',
  Cancelled = 'Cancelled',
  Requested = 'Requested',
  Scheduled = 'Scheduled',
  Arrived = 'Arrived',
  InProgress = 'InProgress',
  Completed = 'Completed'
}

// "Record" in Typescript is a helper meta-type that creates
// a "table" type that maps one type to another!
// In this case StatusTimeline is the shape of an object whose keys are
// of type AppointmentStatus, and whose values are of type Date.
export type StatusTimeline = Record<AppointmentStatus, Date>;

export type ChangeRules = {
  isHappyPath: boolean;
  happyPathOrder: number;
  isStartState: boolean;
  isEndState: boolean;
  canUndo: boolean;
  isOptional?: boolean;
  allowUpdateToFutureDate?: boolean;
  validNextStatuses: AppointmentStatus[];
  /** `null` when undo is not applicable for this status (see `computeUndoStatus`). */
  undoStatus: Nullable<AppointmentStatus>;
  /** Assigned by `getStatusChangeRules()` from happy-path ordering. */
  stepNumber?: number;
};

export type StatusChangeRules = Record<AppointmentStatus, ChangeRules>;

/**
 * A rules table that may omit statuses not applicable to a given appointment type
 * (e.g. Preparation appointments never have Requested/Arrived/NoShow entries).
 */
type AppointmentTypeRules = Partial<Record<AppointmentStatus, ChangeRules>>;

export function getStatusChangeRules(
  optionalStatuses: AppointmentStatus[] = [
    AppointmentStatus.Requested,
    AppointmentStatus.InProgress
  ]
): StatusChangeRules {
  const statusRules: Record<AppointmentStatus, ChangeRules> = {
    [AppointmentStatus.Requested]: {
      isHappyPath: true,
      happyPathOrder: 0.5,
      isStartState: true,
      isEndState: false,
      canUndo: false,
      isOptional: true,
      validNextStatuses: [AppointmentStatus.Scheduled, AppointmentStatus.Cancelled],
      undoStatus: null,
      allowUpdateToFutureDate: true
    },
    [AppointmentStatus.Scheduled]: {
      isHappyPath: true,
      happyPathOrder: 1,
      isStartState: true,
      isEndState: false,
      canUndo: true,
      validNextStatuses: [
        AppointmentStatus.Arrived,
        AppointmentStatus.NoShow,
        AppointmentStatus.Cancelled
      ],
      undoStatus: AppointmentStatus.Requested,
      allowUpdateToFutureDate: true
    },
    [AppointmentStatus.Arrived]: {
      isHappyPath: true,
      happyPathOrder: 2,
      isStartState: false,
      isEndState: false,
      canUndo: true,
      validNextStatuses: [
        AppointmentStatus.InProgress,
        AppointmentStatus.Completed,
        AppointmentStatus.Cancelled
      ],
      undoStatus: AppointmentStatus.Scheduled,
      allowUpdateToFutureDate: true
    },
    [AppointmentStatus.InProgress]: {
      isHappyPath: true,
      happyPathOrder: 2.5,
      isStartState: false,
      isOptional: true,
      isEndState: false,
      canUndo: true,
      validNextStatuses: [AppointmentStatus.Completed],
      undoStatus: AppointmentStatus.Arrived,
      allowUpdateToFutureDate: false
    },
    [AppointmentStatus.Completed]: {
      isHappyPath: true,
      happyPathOrder: 3,
      isStartState: false,
      isEndState: true,
      canUndo: true,
      validNextStatuses: [],
      // WARNING: This is not exactly true! In practice Neutron
      // will choose either Arrived or InProgress dynamically,
      // based on the timeline. See "computeUndoStatus()".
      undoStatus: AppointmentStatus.Arrived,
      allowUpdateToFutureDate: true
    },
    [AppointmentStatus.NoShow]: {
      isHappyPath: false,
      happyPathOrder: 0,
      isStartState: false,
      isEndState: true,
      canUndo: true,
      validNextStatuses: [],
      undoStatus: AppointmentStatus.Scheduled,
      allowUpdateToFutureDate: true
    },
    [AppointmentStatus.Cancelled]: {
      isHappyPath: false,
      happyPathOrder: 0,
      isStartState: false,
      isEndState: true,
      canUndo: false,
      validNextStatuses: [],
      undoStatus: null,
      allowUpdateToFutureDate: true
    }
  };

  // Provide status change rules based on optional statuses given
  const rulesSortedByPathOrder = (Object.keys(statusRules) as AppointmentStatus[]).sort(
    (a, b) => statusRules[a].happyPathOrder - statusRules[b].happyPathOrder
  );

  const nonHappyPathStatusCount = Object.values(statusRules).filter(
    statusRule => !statusRule.isHappyPath
  ).length;

  const happyPathStatusCount =
    rulesSortedByPathOrder.filter(status => {
      const statusRule = statusRules[status];
      return !statusRule.isOptional || (statusRule.isOptional && optionalStatuses.includes(status));
    }).length - nonHappyPathStatusCount;

  let stepCount = 1;
  let nonHappyPathIncrementor = happyPathStatusCount + 1;

  const adjustedRules = {} as StatusChangeRules;

  rulesSortedByPathOrder.forEach((status: AppointmentStatus) => {
    const statusRule = statusRules[status];
    if (statusRule.isOptional) {
      if (optionalStatuses.includes(status)) {
        addStepNumber(stepCount, status, statusRule);
        stepCount++;
      }
    } else if (statusRule.isHappyPath) {
      addStepNumber(stepCount, status, statusRule);
      stepCount++;
    } else {
      addStepNumber(nonHappyPathIncrementor, status, statusRule);
      nonHappyPathIncrementor++;
    }
  });

  function addStepNumber(
    stepNumber: number,
    status: AppointmentStatus,
    statusRule: ChangeRules
  ): void {
    if (stepNumber === 1) {
      statusRule.canUndo = false;
    }
    statusRule.stepNumber = stepNumber;
    adjustedRules[status] = statusRule;
  }

  return adjustedRules satisfies StatusChangeRules;
}

/**
 * Preparation appointments always start at Scheduled, and InProgress is a mandatory step
 * (unlike Standard's optional InProgress): Scheduled -> InProgress -> Completed, with Cancelled
 * reachable from either Scheduled or InProgress. There's no gate check-in (no
 * Requested/Arrived/NoShow), and Cancelled can't be undone. Statuses not listed here are not
 * applicable to Preparation at all (see `isStatusAllowed`). Not exported: every consumer of this
 * table is a function below that takes `type` directly (callers outside this file never need to
 * handle a rules table).
 */
function getPreparationStatusChangeRules(): AppointmentTypeRules {
  return {
    [AppointmentStatus.Scheduled]: {
      isHappyPath: true,
      happyPathOrder: 1,
      isStartState: true,
      isEndState: false,
      canUndo: false,
      validNextStatuses: [AppointmentStatus.InProgress, AppointmentStatus.Cancelled],
      undoStatus: null
    },
    [AppointmentStatus.InProgress]: {
      isHappyPath: true,
      happyPathOrder: 2,
      isStartState: false,
      isEndState: false,
      canUndo: true,
      validNextStatuses: [AppointmentStatus.Completed, AppointmentStatus.Cancelled],
      undoStatus: AppointmentStatus.Scheduled,
      allowUpdateToFutureDate: false
    },
    [AppointmentStatus.Completed]: {
      isHappyPath: true,
      happyPathOrder: 3,
      isStartState: false,
      isEndState: true,
      canUndo: true,
      validNextStatuses: [],
      undoStatus: AppointmentStatus.InProgress
    },
    [AppointmentStatus.Cancelled]: {
      isHappyPath: false,
      happyPathOrder: 0,
      isStartState: false,
      isEndState: true,
      canUndo: false,
      validNextStatuses: [],
      undoStatus: null
    }
  };
}

// The one place that knows how to go from "what type is this appointment" to "which rules
// table applies". Not exported: every function below takes `type` directly, so nothing
// outside this file ever needs to hold a rules table.
function getStatusChangeRulesForType(type: AppointmentType): AppointmentTypeRules {
  return type === AppointmentType.Preparation
    ? getPreparationStatusChangeRules()
    : getStatusChangeRules();
}

// Stand-in for a status that isn't part of the type's rules table at all (e.g. Requested for
// Preparation), treated as an unreachable dead end. Letting `getRule()` return this instead of
// `undefined` means every caller below reads plain fields (`.canUndo`, `.validNextStatuses`, ...)
// instead of guarding each access with `?.`.
const NOT_APPLICABLE_RULE: ChangeRules = {
  isHappyPath: false,
  happyPathOrder: 0,
  isStartState: false,
  isEndState: true,
  canUndo: false,
  validNextStatuses: [],
  undoStatus: null
};

function getRule(status: AppointmentStatus, type: AppointmentType): ChangeRules {
  return getStatusChangeRulesForType(type)[status] ?? NOT_APPLICABLE_RULE;
}

export function getAllStatuses(): AppointmentStatus[] {
  return Object.keys(AppointmentStatus) as AppointmentStatus[];
}

export function getNonOptionalStatuses(): AppointmentStatus[] {
  const all = getAllStatuses();
  const optionals = getOptionalStatuses();

  return all.filter(status => !optionals.includes(status as AppointmentStatus));
}

export function getOptionalStatuses(): AppointmentStatus[] {
  const statusRules = getStatusChangeRules();
  const search = objectFilter(statusRules, key => Boolean(statusRules[key].isOptional));

  return Object.keys(search) as AppointmentStatus[];
}

/*
 * Returns true when a status timeline date is trying to be set
 * and the current status does not allow this date to be in the
 * future. Check allowUpdateToFutureDate property on the
 * getStatusChangeRules() settings for more details.
 * In general it always should return false, representing a
 * valid timeline change.
 */
export function isTimelineStatusChangeDateInvalid(
  status: AppointmentStatus,
  timeStamp: string,
  type: AppointmentType
): boolean {
  const shouldValidateFuture = getRule(status, type).allowUpdateToFutureDate === false;
  if (isDateString(timeStamp) && shouldValidateFuture) {
    return isFutureDate(timeStamp);
  }
  return false;
}

export function isOptionalStatus(status: AppointmentStatus, type: AppointmentType): boolean {
  return Boolean(getRule(status, type).isOptional);
}

export function isStartStatus(status: AppointmentStatus, type: AppointmentType): boolean {
  return getStartStatuses(type).includes(status);
}

export function isUndoAllowed(status: AppointmentStatus, type: AppointmentType): boolean {
  return validateAppointmentStatus(status) && getRule(status, type).canUndo;
}

export function isRescheduleAllowed(status: AppointmentStatus, type: AppointmentType): boolean {
  return isStartStatus(status, type);
}

export function isCancelAllowed(status: AppointmentStatus, type: AppointmentType): boolean {
  return getRule(status, type).validNextStatuses.includes(AppointmentStatus.Cancelled);
}

// map conditions in which the status time must be later than the previous status:
// Scheduled time must be later than requested time (start statuses)
// No-start statuses must be later than the previous and don't are affected by start statuses
export function isChronologyEnforced(
  currentStatus: AppointmentStatus,
  previousStatus: AppointmentStatus,
  type: AppointmentType
): boolean {
  return (
    (isStartStatus(currentStatus, type) && isStartStatus(previousStatus, type)) ||
    (!isStartStatus(currentStatus, type) && !isStartStatus(previousStatus, type))
  );
}

export function getStartStatuses(type: AppointmentType): AppointmentStatus[] {
  const rules = getStatusChangeRulesForType(type);
  const search = objectFilter(rules, key => getRule(key, type).isStartState);

  return Object.keys(search) as AppointmentStatus[];
}

// Returns the status that appointments get automatically CREATED with. This used to
// always be "Scheduled", but now has the option of being "Requested" as well.
export function getCreationStatusFromSettings(
  settings: ISettings,
  type: AppointmentType
): AppointmentStatus {
  const validStatuses = getStartStatuses(type);
  const statusFromSetting = settings?.appointmentCreationStatus as AppointmentStatus;
  return validStatuses.includes(statusFromSetting)
    ? statusFromSetting
    : AppointmentStatus.Scheduled;
}

export function getReschedulableStatuses(type: AppointmentType): AppointmentStatus[] {
  const rules = getStatusChangeRulesForType(type);
  const search = objectFilter(rules, key => isRescheduleAllowed(key, type));

  return Object.keys(search) as AppointmentStatus[];
}

export function validateAppointmentStatus(status: AppointmentStatus): boolean {
  return status && Object.keys(AppointmentStatus).includes(status);
}

// Is `status` applicable to this appointment type at all (regardless of any particular
// transition)? E.g. Requested is not applicable to Preparation appointments.
export function isStatusAllowed(status: AppointmentStatus, type: AppointmentType): boolean {
  return status in getStatusChangeRulesForType(type);
}

export function isStatusTransitionAllowed(
  from: AppointmentStatus,
  to: AppointmentStatus,
  type: AppointmentType
): boolean {
  if (
    !(
      validateAppointmentStatus(from) &&
      validateAppointmentStatus(to) &&
      isStatusAllowed(from, type)
    )
  ) {
    return false;
  }
  return from === to || getRule(from, type).validNextStatuses.includes(to);
}

function validateTimelineStatusKeys(inputStatusTimeLineKeys: string[]): Nullable<string> {
  if (!inputStatusTimeLineKeys.every(k => getAllStatuses().includes(k as AppointmentStatus))) {
    return `statusTimeline must only contain a subset of the following fields: ${getAllStatuses()}`;
  }
  return null;
}

function validateTimelineMandatoryFields(inputStatusTimeLineKeys: string[]): Nullable<string> {
  if (
    !getNonOptionalStatuses().every(mandatoryStatus =>
      inputStatusTimeLineKeys.includes(mandatoryStatus)
    )
  ) {
    return `statusTimeline must at least contain the following fields: ${getNonOptionalStatuses()}`;
  }
  return null;
}

function validateTimelineFieldDates(inputStatusTimeline: StatusTimeline): Nullable<string> {
  for (const val of Object.values(inputStatusTimeline)) {
    const isEmptyDate = val?.toString().length === 0;
    const isInvalidDate = !isNil(val) && !(isDate(val) || isDateString(val));
    if (isEmptyDate || isInvalidDate) {
      return `statusTimeline: '${val}' is not a valid Date string`;
    }
  }
  return null;
}

function validateTimelineStatusesApplicable(
  inputStatusTimeline: StatusTimeline,
  type: AppointmentType
): Nullable<string> {
  for (const [status, val] of Object.entries(inputStatusTimeline)) {
    if (val && !isStatusAllowed(status as AppointmentStatus, type)) {
      return `statusTimeline: '${status}' is not applicable to ${type} appointments`;
    }
  }
  return null;
}

function validateTimelineStartStatusCount(
  inputStatusTimeline: StatusTimeline,
  inputStatusTimeLineKeys: string[],
  type: AppointmentType
): Nullable<string> {
  const startStatuses = inputStatusTimeLineKeys.filter(
    k =>
      Boolean(inputStatusTimeline[k as AppointmentStatus]) &&
      getStartStatuses(type).includes(k as AppointmentStatus)
  );

  if (startStatuses.length < 1 || startStatuses.length > getStartStatuses(type).length) {
    return `statusTimeline: Incorrect number of start Statuses: found ${startStatuses.length}`;
  }
  return null;
}

function getHappyPathStatusesInOrder(type: AppointmentType): AppointmentStatus[] {
  return map(
    reduce(
      getStatusChangeRulesForType(type),
      (result, _value, key) => {
        const rule = getRule(key as AppointmentStatus, type);
        if (rule.isHappyPath) {
          result.push({ key: key as AppointmentStatus, happyPathOrder: rule.happyPathOrder });
        }
        return sortBy(result, 'happyPathOrder');
      },
      [] as Array<{ key: AppointmentStatus; happyPathOrder: number }>
    ),
    'key'
  ) as AppointmentStatus[];
}

function timelineMakeDate(date: Maybe<DateLike>): Maybe<Date> {
  if (!date) {
    return null;
  }

  try {
    return new NovaDate(date).toJSDate();
  } catch {
    return null;
  }
}

function validateTimelineHappyPathChronology(
  inputStatusTimeline: StatusTimeline,
  type: AppointmentType
): Nullable<string> {
  const statusHappyPath = getHappyPathStatusesInOrder(type);
  const nonNullStatusHappyPath = statusHappyPath.filter(status => {
    return inputStatusTimeline[status] !== null;
  });

  // Enforce chronology on the full happy path and again with optional NULL statuses removed
  // (needed for optional InProgress).
  for (const path of [statusHappyPath, nonNullStatusHappyPath]) {
    for (let i = 1; i < path.length; i++) {
      const currentStatusName = path[i];
      const previousStatusName = path[i - 1];
      const currentStatusDateTime = timelineMakeDate(inputStatusTimeline[currentStatusName]);
      const previousStatusDateTime = timelineMakeDate(inputStatusTimeline[previousStatusName]);
      const hasCronEnforcement = isChronologyEnforced(currentStatusName, previousStatusName, type);
      const previousStatusIsOptional = isOptionalStatus(previousStatusName, type);

      const curr = currentStatusDateTime;
      const prev = previousStatusDateTime;
      if (
        hasCronEnforcement &&
        isDate(curr) &&
        (!(previousStatusIsOptional || prev) || (isDate(prev) && curr.getTime() < prev.getTime()))
      ) {
        return `statusTimeline: You cannot mark an Appointment ${currentStatusName} before ${previousStatusName}`;
      }
    }
  }
  return null;
}

function validateTimelineFutureDateRules(
  inputStatusTimeline: StatusTimeline,
  type: AppointmentType
): Nullable<string> {
  for (const [status, val] of Object.entries(inputStatusTimeline)) {
    if (isTimelineStatusChangeDateInvalid(status as AppointmentStatus, String(val), type)) {
      return `statusTimeline: '${status}' status timeline cannot be changed into a future date ${val}'`;
    }
  }
  return null;
}

// Type-independent checks only (keys, mandatory fields, date formats); for callers with no
// reliable appointment type, e.g. DTO validators. See validateStatusTimeline for the rest.
export function validateStatusTimelineShape(inputStatusTimeline: StatusTimeline): Nullable<string> {
  if (isNil(inputStatusTimeline)) {
    return 'statusTimeline must be a valid StatusTimeline object';
  }
  if (!isObject(inputStatusTimeline)) {
    return 'statusTimeline must be a valid StatusTimeline object';
  }

  const inputStatusTimeLineKeys = Object.keys(inputStatusTimeline);

  return (
    validateTimelineStatusKeys(inputStatusTimeLineKeys) ??
    validateTimelineMandatoryFields(inputStatusTimeLineKeys) ??
    validateTimelineFieldDates(inputStatusTimeline) ??
    null
  );
}

export function validateStatusTimeline(
  inputStatusTimeline: StatusTimeline,
  type: AppointmentType
): Nullable<string> {
  const shapeError = validateStatusTimelineShape(inputStatusTimeline);
  if (shapeError) {
    return shapeError;
  }

  const inputStatusTimeLineKeys = Object.keys(inputStatusTimeline);

  return (
    validateTimelineStatusesApplicable(inputStatusTimeline, type) ??
    validateTimelineStartStatusCount(inputStatusTimeline, inputStatusTimeLineKeys, type) ??
    validateTimelineHappyPathChronology(inputStatusTimeline, type) ??
    validateTimelineFutureDateRules(inputStatusTimeline, type) ??
    null
  );
}

export function getStatusesInHappyPathOrder(type: AppointmentType): string[] {
  return Object.entries(getStatusChangeRulesForType(type))
    .sort(([_a, aValue], [_b, bValue]) => {
      return aValue.happyPathOrder > 0 ? aValue.happyPathOrder - bValue.happyPathOrder : 1;
    })
    .map(([key, _]) => {
      return key;
    });
}

export function getMostRecentStatusChange(statusTimeline: StatusTimeline): string {
  if (!isObject(statusTimeline)) {
    return '';
  }

  // TODO: What happens if an API call sets the timeline to the same value across the board for whatever reason?
  let statuses = Object.entries(statusTimeline)
    .filter(([_, value]) => Boolean(value))
    .map(([key, value]) => ({
      status: key,
      time: value
    }));
  statuses = sortBy(statuses, 'time').reverse();

  return statuses?.length > 0 ? statuses[0].status : '';
}

export function getMostRecentStatusChangeByHappyPath(
  statusTimeline: StatusTimeline,
  type: AppointmentType
): string {
  if (!isObject(statusTimeline)) {
    return '';
  }

  const statusesInReverseHappyPathOrder = getStatusesInHappyPathOrder(type).reverse();
  const statuses = Object.entries(statusTimeline)
    .filter(([_, value]) => Boolean(value))
    .map(([key]) => key);
  let lastStatus = '';

  statusesInReverseHappyPathOrder.some(status => {
    if (statuses.includes(status)) {
      lastStatus = status;
      return true;
    }
    return false;
  });

  return lastStatus;
}

// Returns true if status exists in the timeline and is set to a
// truthy value.
export function isStatusSetInStatusTimeline(
  status: AppointmentStatus,
  timeline: StatusTimeline
): boolean {
  const keys = Object.keys(timeline);
  return keys.includes(status) && Boolean(timeline[status]);
}

export function computeUndoStatus(
  status: AppointmentStatus,
  timeline: StatusTimeline,
  type: AppointmentType
): Nullable<AppointmentStatus> {
  // Special case for "Completed" since it depends
  // on whether we "went through" the InProgress status.
  if (status === AppointmentStatus.Completed && timeline[AppointmentStatus.InProgress]) {
    return AppointmentStatus.InProgress;
  }

  return getRule(status, type).undoStatus;
}
